import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SSE_EVENTS } from '@nlinks/shared';
import { eventStreamUrl, onAuthChange } from './api';
import { queryKeys } from './queries';

export type LiveStatus = 'connecting' | 'live' | 'offline';

/**
 * Subscribes the open dashboard to worker activity.
 *
 * Invalidations are debounced: a mailbox sync can finish 80 link visits in a
 * few seconds, and refetching the dashboard 80 times would be far more
 * expensive than the work being reported.
 */
export function useLiveEvents(enabled = true): LiveStatus {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LiveStatus>('connecting');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Bumped whenever the token pair changes.
   *
   * EventSource cannot send an Authorization header, so the access token is
   * baked into the stream URL. That URL therefore goes stale the moment the
   * token rotates — roughly every fifteen minutes — and the browser's automatic
   * reconnect would keep retrying a URL the server now rejects, leaving the
   * dashboard permanently "offline". Re-running the effect rebuilds the URL
   * with the current token instead.
   */
  const [tokenEpoch, setTokenEpoch] = useState(0);
  useEffect(() => onAuthChange(() => setTokenEpoch((n) => n + 1)), []);

  useEffect(() => {
    if (!enabled) return;

    const url = eventStreamUrl();
    if (!url) {
      setStatus('offline');
      return;
    }

    const source = new EventSource(url);

    const scheduleInvalidate = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.keywords });
        void queryClient.invalidateQueries({ queryKey: queryKeys.mailAccounts });
      }, 1200);
    };

    source.onopen = () => setStatus('live');
    source.onerror = () => {
      // EventSource reconnects on its own; surface the gap rather than fighting it.
      setStatus('offline');
    };

    for (const eventName of Object.values(SSE_EVENTS)) {
      source.addEventListener(eventName, () => {
        setStatus('live');
        scheduleInvalidate();
      });
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      source.close();
    };
  }, [enabled, queryClient, tokenEpoch]);

  return status;
}
